import React from 'react';
import { AbsoluteCenter, Box, ProgressCircle } from '@chakra-ui/react';
import { LuBookOpen, LuCircleAlert } from 'react-icons/lu';
import ShimmerOverlay from '@/components/common/ShimmerOverlay';
import { DELAYED_READINESS_SHIMMER_DURATION_MS } from '@/components/ui/motion';
import {
	hasKnowledgeBaseFailure,
	KnowledgeBaseReadinessState,
	type KnowledgeBaseReadinessState as KnowledgeBaseReadinessStateValue,
} from '@/hooks/house/documents/knowledgeBaseReadiness';

interface BookProgressIconProps {
	value: number;
	state: KnowledgeBaseReadinessStateValue;
}

/**
 * Book icon with a live circular progress ring driven by the API pipeline status.
 * Shows a shimmer sweep animation while indexing is in progress to signal activity
 * even when the progress wheel appears stalled.
 */
const BookProgressIcon: React.FC<BookProgressIconProps> = (props) => {
	const { value, state } = props;
	const failed = hasKnowledgeBaseFailure(state);
	const stalled = state === KnowledgeBaseReadinessState.Stalled;
	const needsAttention = failed || stalled;
	const attentionColor = stalled ? 'orange.600' : 'text.error';
	const bookOrFailureIcon = (
		<Box
			color={needsAttention ? attentionColor : 'brand.fg'}
			position="relative"
			display="flex"
			overflow="hidden"
			lineHeight={0}
		>
			{needsAttention ? <LuCircleAlert size={18} /> : <LuBookOpen size={18} />}
			{!needsAttention && (
				<ShimmerOverlay
					style={
						state === KnowledgeBaseReadinessState.Delayed
							? { animationDuration: `${DELAYED_READINESS_SHIMMER_DURATION_MS}ms` }
							: undefined
					}
				/>
			)}
		</Box>
	);

	return (
		<Box position="relative" display="flex" flexShrink={0} lineHeight={0}>
			<ProgressCircle.Root size="md" value={value}>
				<ProgressCircle.Circle css={{ '--thickness': '4px' }}>
					<ProgressCircle.Track stroke={stalled ? 'orange.200' : failed ? 'red.200' : undefined} />
					<ProgressCircle.Range stroke={needsAttention ? attentionColor : 'brand.fg'} />
				</ProgressCircle.Circle>
				<AbsoluteCenter>{bookOrFailureIcon}</AbsoluteCenter>
			</ProgressCircle.Root>
		</Box>
	);
};

export default BookProgressIcon;
